import React from 'react';
import {
    BookOpen,
    ChevronRight,
    ChevronDown,
    Search,
    X,
} from 'lucide-react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';

export interface GuideSection {
    id: string;
    title: string;
    icon: React.ReactNode;
    subsections?: { id: string; title: string }[];
}

interface UserGuideSidebarProps {
    guideSections: GuideSection[];
    activeSection: string;
    expandedSections: string[];
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    toggleSection: (id: string) => void;
    scrollToSection: (id: string) => void;
}

export const UserGuideSidebar: React.FC<UserGuideSidebarProps> = ({
    guideSections,
    activeSection,
    expandedSections,
    searchTerm,
    setSearchTerm,
    toggleSection,
    scrollToSection,
}) => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    return (
        <aside className={clsx(
            'w-72 fixed left-64 top-20 bottom-0 overflow-y-auto border-r p-4 z-40',
            isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'
        )}>
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <h2 className={clsx('text-lg font-bold flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                        <BookOpen size={20} className="text-blue-500" />
                        User Guide
                    </h2>
                    <button
                        onClick={() => navigate(-1)}
                        className={clsx(
                            'p-2 rounded-lg transition-colors',
                            isDark ? 'hover:bg-slate-800 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-800'
                        )}
                        title="Close Guide"
                    >
                        <X size={20} />
                    </button>
                </div>
                <p className="text-sm text-slate-500">Documentation & App Notes</p>
            </div>

            {/* Search */}
            <div className="relative mb-4">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                    type="text"
                    placeholder="Search guide..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={clsx(
                        'w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                        isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-slate-50 border-slate-200'
                    )}
                />
            </div>

            {/* Navigation */}
            <nav className="space-y-1">
                {guideSections.map(section => (
                    <div key={section.id}>
                        <button
                            onClick={() => {
                                toggleSection(section.id);
                                scrollToSection(section.id);
                            }}
                            className={clsx(
                                'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                                activeSection === section.id || activeSection.startsWith(section.id)
                                    ? 'bg-blue-500 text-white'
                                    : isDark
                                        ? 'text-slate-300 hover:bg-slate-800'
                                        : 'text-slate-700 hover:bg-slate-100'
                            )}
                        >
                            <span className="flex items-center gap-2">
                                {section.icon}
                                {section.title}
                            </span>
                            {section.subsections && (
                                expandedSections.includes(section.id)
                                    ? <ChevronDown size={16} />
                                    : <ChevronRight size={16} />
                            )}
                        </button>
                        {section.subsections && expandedSections.includes(section.id) && (
                            <div className="ml-6 mt-1 space-y-1">
                                {section.subsections.map(sub => (
                                    <button
                                        key={sub.id}
                                        onClick={() => scrollToSection(sub.id)}
                                        className={clsx(
                                            'w-full text-left px-3 py-1.5 rounded text-xs transition-colors',
                                            activeSection === sub.id
                                                ? 'text-blue-500 font-medium'
                                                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                        )}
                                    >
                                        {sub.title}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </nav>
        </aside>
    );
};
